import { apiAxios } from '@/api'
import { ADVISER_API_PATH, ADVISER_CHAT_URL } from './constants'
import type {
  AttachmentRef,
  AttachmentUploadError,
  AttachmentUploader,
} from './attachments.types'

const DEFAULT_CONTENT_TYPE = 'application/octet-stream'
const GENERIC_FAILURE = 'The connection dropped.'

const uploadError = (
  message: string,
  percent: number,
  aborted = false
): AttachmentUploadError =>
  Object.assign(new Error(message), { percent, aborted })

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const isAttachmentRef = (value: unknown): value is AttachmentRef =>
  typeof value === 'object' &&
  value !== null &&
  'token' in value &&
  typeof value.token === 'string' &&
  'fileName' in value &&
  typeof value.fileName === 'string' &&
  'size' in value &&
  typeof value.size === 'number' &&
  'contentType' in value &&
  typeof value.contentType === 'string'

const errorBodyMessage = (value: unknown): string | null =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof value.message === 'string'
    ? value.message
    : null

export const createZendeskAttachmentUploader = (): AttachmentUploader => ({
  upload: (file, onProgress, signal) =>
    new Promise<AttachmentRef>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      let percent = 0

      xhr.open(
        'POST',
        `${ADVISER_CHAT_URL}/attachments?fileName=${encodeURIComponent(file.name)}`
      )
      xhr.withCredentials = true
      xhr.setRequestHeader('Content-Type', file.type || DEFAULT_CONTENT_TYPE)

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return
        percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      }

      xhr.onload = () => {
        const body = parseJson(xhr.responseText)

        if (xhr.status >= 200 && xhr.status < 300) {
          if (isAttachmentRef(body)) {
            resolve(body)
            return
          }
          reject(uploadError(GENERIC_FAILURE, percent))
          return
        }

        reject(uploadError(errorBodyMessage(body) ?? GENERIC_FAILURE, percent))
      }

      xhr.onerror = () => reject(uploadError(GENERIC_FAILURE, percent))
      xhr.onabort = () =>
        reject(uploadError('Upload cancelled.', percent, true))

      signal.addEventListener('abort', () => xhr.abort())
      xhr.send(file)
    }),

  remove: async ({ token }) => {
    // POST with the token in the body: it is a live credential for the file,
    // and a path segment lands in the request log.
    await apiAxios.post<unknown>(`${ADVISER_API_PATH}/attachments/discard`, {
      token,
    })
  },
})
